import React, { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { useToast } from '../contexts/ToastContext';

interface InviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  shopId: string | null;
}

const InviteModal: React.FC<InviteModalProps> = ({ isOpen, onClose, shopId }) => {
  const toast = useToast();
  const [link, setLink] = useState<string>("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const generate = async () => {
      if (!isOpen || !shopId) return;
      setLoading(true);
      setLink("");
      try {
        const { data, error } = await supabase.functions.invoke("shop-invite-create", {
          body: { shop_id: shopId, expires_in_hours: 72 }, // default 3 days
        });
        if (error) throw error;
        const token = (data as any)?.token as string;
        if (!token) throw new Error("Failed to generate invite token");
        const url = `${window.location.origin}/accept-invite?token=${encodeURIComponent(token)}`;
        setLink(url);
      } catch (e: any) {
        toast.error(e?.message || "Failed to create invite link");
      } finally {
        setLoading(false);
      }
    };
    generate();
  }, [isOpen, shopId]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />
      {/* Modal */}
      <div className="relative modal-body rounded-2xl w-full max-w-lg mx-4">
        <div className="p-4 border-b border-white/[0.08] flex items-center justify-between">
          <h3 className="text-lg font-semibold  text-white">Invite teammate</h3>
          <button onClick={onClose} className="text-white/35 hover:text-white/70  text-sm">Close</button>
        </div>
        <div className="p-4">
          <p className="text-sm text-white/50  mb-3">Share this link to grant access to your shop. It expires in 3 days.</p>
          <div className="flex gap-2 items-center">
            <input
              type="text"
              readOnly
              value={loading ? "Generating..." : (link || "")}
              className="flex-1 px-3 py-2 bg-transparent border border-white/15 text-white  text-xs"
            />
            <button
              disabled={loading || !link}
              onClick={() => link && navigator.clipboard.writeText(link)}
              className="px-3 py-2 border border-white/15  hover:bg-white/[0.08]  text-sm disabled:opacity-50"
            >
              Copy
            </button>
          </div>
        </div>
        <div className="p-3 border-t border-white/[0.08] flex justify-end">
          <button onClick={onClose} className="px-4 py-2 bg-orange-500 text-white border-2 border-orange-500 hover:bg-orange-400 rounded-xl transition-colors text-sm">Done</button>
        </div>
      </div>
    </div>
  );
};

export default InviteModal;
